import { useEffect } from 'react';
import {
  Navigate,
  matchRoutes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Spin } from 'antd';
import routers from './routerConfig';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectUserInfo, setUserInfo } from '@/store/feature/userSlice';
import { Role } from '@/api/interface';
import { getUserRole } from '@/api/user/login';
import Cookies from 'js-cookie';

type Props = {
  children: React.ReactNode;
};

function AuthRoute(props: Props) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(selectUserInfo);
  const router = useNavigate();
  const { children } = props;
  const storedToken = localStorage.getItem('token');
  const picaToken = Cookies.get('picaToken');
  const isLogin = Boolean(storedToken || picaToken);

  if (!storedToken && picaToken) {
    localStorage.setItem('token', picaToken);
  }

  useEffect(() => {
    if (!isLogin || userInfo.userType !== Role.UNKNOWN) {
      return;
    }
    getUserRole()
      .then((response) => {
        dispatch(setUserInfo(response.data));
      })
      .catch(() => {
        localStorage.removeItem('token');
        router('/login', { replace: true });
      });
  }, [dispatch, isLogin, router, userInfo.userType]);

  useEffect(() => {
    const userType = userInfo.userType;
    if (!isLogin || userType === Role.UNKNOWN) {
      return;
    }
    if (location.pathname === '/') {
      switch (userType) {
        case Role.PARTNER:
        case Role.ADMIN:
        case Role.TEST_LICENSE:
          router('/admin', { replace: true });
          break;
        case Role.USER:
          router('/licenses', { replace: true });
          break;
        default:
          router('/login', { replace: true });
          break;
      }
      return;
    }

    const isTestLicensePath = location.pathname.startsWith(
      '/admin/test-license'
    );
    if (
      userType === Role.TEST_LICENSE &&
      location.pathname.startsWith('/admin/') &&
      !isTestLicensePath
    ) {
      router('/admin', { replace: true });
      return;
    }
    if (userType !== Role.TEST_LICENSE && isTestLicensePath) {
      router('/unauthorized', { replace: true });
      return;
    }

    const routes = matchRoutes(routers, location);
    if (routes === null) {
      return;
    }
    const isAllowedRoute = routes.some((route) =>
      route.route.meta.role?.includes(userType)
    );
    if (!isAllowedRoute) {
      router('/unauthorized', { replace: true });
    }
  }, [isLogin, location.pathname, router, userInfo.userType]);

  if (!isLogin) {
    return <Navigate to="/login" replace />;
  }
  if (userInfo.userType === Role.UNKNOWN) {
    return <Spin size="large" fullscreen />;
  }
  return children as React.ReactElement;
}

export default AuthRoute;
